import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "./fordon";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { fritidFaq, fritidSections } from "@/data/hub-content";

export const Route = createFileRoute("/fritid")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(fritidFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Fritidsprodukter & tillbehör – AK Husbil & Fritid, Kalix" },
      {
        name: "description",
        content:
          "Förtält, el, kök, förvaring och säkerhet — beprövade tillbehör för husbilen och husvagnen. Personlig rådgivning från Kalix.",
      },
      { name: "keywords", content: "husbilstillbehör, husvagnstillbehör, förtält, markis, campingutrustning Kalix" },
      { property: "og:title", content: "Fritidsprodukter & tillbehör – AK Husbil & Fritid" },
      {
        property: "og:description",
        content: "Beprövade tillbehör för husbilen och husvagnen — sålt av folk som själva lever på vägen.",
      },
      { property: "og:url", content: "https://www.akhusbilfritid.se/fritid" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "Fritidsprodukter och tillbehör för husbil och husvagn" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/fritid" }],
    scripts: [
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
    ],
  }),
  component: FritidPage,
});

const cats = [
  { n: "01", t: "Förtält & Markiser" },
  { n: "02", t: "El & Energi" },
  { n: "03", t: "Kök & Grill" },
  { n: "04", t: "Förvaring" },
  { n: "05", t: "Säkerhet" },
  { n: "06", t: "Komfort" },
];

function FritidPage() {
  return (
    <>
      <PageHeader
        label="Tillbehör · Beprövat på vägen"
        title={<>Utrustning som <span className="font-italic-display text-accent">håller</span>.</>}
        sub="Vi säljer bara tillbehör vi själva använder — förtält, el, kök, förvaring och säkerhet. Hittar du inte det du söker? Ring oss, vi tar hem det åt dig."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            Handla på Fyndanu <ArrowUpRight size={14} />
          </a>
          <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Fråga oss <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <section className="container-edge py-20 md:py-24">
        <p className="eyebrow">Kategorier</p>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 md:grid-cols-3">
          {cats.map((c) => (
            <div key={c.n} className="group flex aspect-[4/3] flex-col justify-between rounded-3xl border border-border bg-card p-6 hover-lift">
              <span className="text-xs font-medium tracking-wider text-accent">{c.n}</span>
              <div className="flex items-end justify-between">
                <p className="font-display text-2xl md:text-3xl">{c.t}</p>
                <ArrowUpRight size={20} className="text-muted-foreground transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
              </div>
            </div>
          ))}
        </div>
      </section>

      <HubEditorial
        eyebrow="Köpguide · Tillbehör"
        sections={fritidSections}
        faq={fritidFaq}
        faqTitle="Vanliga frågor om tillbehör"
      />

      <section className="container-edge pb-24">
        <div className="rounded-3xl bg-primary p-10 text-primary-foreground md:p-16 elev-shadow">
          <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
            <h2 className="font-display text-3xl md:text-5xl">
              Saknar du<br />något <span className="font-italic-display text-accent">specifikt</span>?
            </h2>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-medium text-accent-foreground transition hover:opacity-95"
              >
                Se Fyndanu <ArrowUpRight size={16} />
              </a>
              <Link
                to="/kontakt"
                className="group inline-flex items-center gap-2 rounded-full border border-background/30 bg-transparent px-6 py-3.5 text-sm font-medium text-primary-foreground transition hover:bg-background/10"
              >
                Fråga oss <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
